<template>
  <div>
    <!-- 觸發 Modal 的按鈕 -->
    <button class="btn btn-green" @click="showModal">Withdraw funds now</button>

    <!-- Modal -->
    <div class="modal fade" ref="staffMemberModal" tabindex="-1" role="dialog" aria-labelledby="exampleModalLabel">
      <div class="modal-dialog modal-xl" role="document">
        <!-- 固定高度的 modal-content，使用 flex 讓上下分區 -->
        <div class="modal-content d-flex flex-column">
          <!-- 中間內容區：根據 currentStep 切換 -->
          <div class="flex-grow-1 p-3">
            <!-- Step 1: 選擇交易清單 -->
            <template v-if="currentStep === 1">
              <h5 class="modal-title mb-3">Withdraw funds now</h5>
              <div class="form-group">
                <div class="d-flex justify-content-center align-items-center mb-0">
                  <div class="card-title">Select individual transactions or select all</div>
                </div>
                <div class="bg-card pt-0">
                  <div class="p-3">
                    <div class="d-flex align-items-center p-0 m-0">
                      <div class="d-flex align-items-center date-range">
                        <div class="d-flex align-items-center">
                          <span class="text-label">From&nbsp;</span>
                          <input
                            type="date"
                            class="form-control custom-date-input"
                            v-model="withdrawStore.filters.from"
                            @change="searchTransactions()"
                          />
                        </div>
                        <div class="d-flex align-items-center gap-1 ms-3">
                          <span class="text-label">&nbsp;to&nbsp;</span>
                          <input
                            type="date"
                            class="form-control custom-date-input"
                            v-model="withdrawStore.filters.to"
                            @change="searchTransactions()"
                          />
                        </div>
                      </div>
                      <!-- 全選 checkbox -->
                      <div class="ms-auto d-flex align-items-center gap-2 select-all-block select-all">
                        <label class="select-all-label mb-0">Select all</label>
                        <input
                          type="checkbox"
                          class="select-all-checkbox"
                          v-model="selectAll"
                        />
                      </div>
                    </div>

                    <div class="table-content">
                      <div class="table-responsive">
                        <!-- 沒有交易紀錄時的顯示 -->
                        <div v-if="withdrawStore.transactions.length === 0" class="no-transactions">
                          <div class="no-transactions-icon">📋</div>
                          <div class="no-transactions-title">No transactions found</div>
                          <div class="no-transactions-desc">There are no transactions available for withdrawal in the selected date range.</div>
                        </div>
                        
                        <!-- 有交易紀錄時顯示表格 -->
                        <table v-else class="table table-hover">
                          <thead>
                            <tr>
                              <th>Transaction Date</th>
                              <th>Transaction total</th>
                              <th>Select</th>
                            </tr>
                          </thead>
                          <tbody>
                            <!-- ★★ 每筆交易的 checkbox ★★ -->
                            <tr
                              v-for="(transaction, index) in paginatedTransactions"
                              :key="transaction.id"
                            >
                              <td>{{ transaction.transaction_date_time }}</td>
                              <td>$ {{ formatAmount(transaction.transaction_total) }}</td>
                              <td>
                                <!-- ★ 新增防盜機制：大於 400 禁止勾選，負值自動勾選且不得取消 -->
                                <input
                                  type="checkbox"
                                  class="select-all-checkbox"
                                  v-model="transaction.checked"
                                  :disabled="transaction.transaction_total > 400 || transaction.transaction_total < 0" 
                                />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div class="pagination-wrapper" v-if="withdrawStore.transactions.length > 0">
                      <div class="pagination-container">
                        <button @click="changePage(1)" :disabled="currentPage === 1">
                          <img src="@/assets/images/admin/arrow/prev-end.png" alt="Prev-end" style="width: 25px; height: 25px;" />
                        </button>
                        <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1">
                          <img src="@/assets/images/admin/arrow/prev.png" alt="Prev" style="width: 25px; height: 25px;" />
                        </button>
                        <span>Page 
                          <input type="number" v-model.number="currentPage" @change="changePage(currentPage)" />
                          of {{ totalPages }}
                        </span>
                        <button @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages">
                          <img src="@/assets/images/admin/arrow/next.png" alt="Next" style="width: 25px; height: 25px;" />
                        </button>
                        <button @click="changePage(totalPages)" :disabled="currentPage === totalPages">
                          <img src="@/assets/images/admin/arrow/next-end.png" alt="Next-end" style="width: 25px; height: 25px;" />
                        </button>
                      </div>
                      <div class="pagination-summary">
                        Displaying transactions {{ startTransaction }} – {{ endTransaction }} of {{ totalEntries }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 下方 Payment info 表格：依據勾選動態計算 -->
                <div class="mt-2" v-if="withdrawStore.transactions.length > 0">
                  <div class="bg-card m-0 flex-grow-1 card-payment-info p-3 mt-3">
                    <div class="p-3">
                      <h5>Payment info</h5>
                      <div class="table-responsive">
                        <table class="table table-bordered table-hover">
                          <thead class="thead-light">
                            <tr>
                              <th>Transactions</th>
                              <th>Transactions subtotal</th>
                              <th>Fees & Refunds</th>
                              <th>
                                <div class="total-text">Total</div>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>{{ selectedCount }}</td>
                              <td>$ {{ formatAmount(selectedSubTotal) }}</td>
                              <td >$ {{ formatAmount(selectedFees) }}</td>
                              <td>
                                <div class="total-text">
                                  $ {{ formatAmount(selectedTotal) }}
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div class="fee-note d-flex justify-content-end">
                        * The ${{ formatAmount(withdrawStore.feesAndRefunds)}} fee/reversal for this withdrawal is included in fees & refunds. No fee applies for withdrawal to card.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!-- Step 2: Review and confirm -->
            <template v-else-if="currentStep === 2">
              <h5 class="modal-title mb-3">Withdraw funds now</h5>
              <div class="review-title">Review and confirm</div>

              <!-- 新增一個容器，用 flex 將內容置中 -->
              <div class="flex-grow-1 d-flex align-items-center justify-content-center flex-column h-50">
                <div class="review-content mt-4 text-center">
                  <div class="d-flex justify-content-start align-items-center">
                    <div class="withdraw-label me-2">Total funds to withdraw</div>
                    <!-- 使用格式化後的金額 -->
                    <div class="withdraw-amount ms-5">$ {{ formatAmount(withdrawStore.checkData.total_funds_to_withdraw) }}</div>
                  </div>
                  <hr/>
                  <div class="d-flex justify-content-start align-items-center mb-3">
                    <div class="withdraw-label">From</div>
                    <div class="ms-5">{{ withdrawStore.checkData.from }}</div>
                  </div>
                  <hr/>
                  <div class="d-flex justify-content-start align-items-center mb-1">
                    <div class="withdraw-label">To</div>
                    <div class="ms-5">{{ withdrawStore.checkData.to }}</div>
                    <div class="ms-5 me-4 bank-account" v-if="withdrawStore.checkData.to_account_type === '1'">
                      <div class="text-left">Bank account:</div>
                      {{ withdrawStore.checkData.to_account_no }}
                    </div>
                  </div>
                  <div class="mt-5 text-center bank-hint">
                    <div>Incorrect Bank account or PayID details?</div>
                    Go to your <a href="#" @click="goToProfiile">profile page</a> to change your nominated account.
                  </div>
                </div>
              </div>
            </template>

            <!-- Step 3: Loading -->
            <template v-else-if="currentStep === 3">
              <div class="flex-grow-1 d-flex justify-content-center align-items-center" style="height: 600px;">
                <div class="spinner"></div>
              </div>
            </template>

            <!-- Step 4: Success -->
            <template v-else-if="currentStep === 4">
              <h5 class="modal-title mb-3">Withdraw funds now</h5>
              <div class="review-title">
                <div class="success-icon">✔</div>
                <div class="ms-3">Withdrawal successful</div>
              </div>

              <!-- 新增一個容器，用 flex 將內容置中 -->
              <div class="flex-grow-1 d-flex align-items-center justify-content-center flex-column h-50">
                <div class="review-content mt-4 text-center">
                  <div class="mb-5">
                    <div class="withdraw-amount">$ {{ formatAmount(withdrawStore.confirmData.payment_amount) }}</div>
                    <div class="mt-2">{{ nowTime }}</div>
                  </div>
                  <hr/>
                  <div class="d-flex justify-content-start align-items-center mt-3  mb-3">
                    <div class="withdraw-label">To</div>
                    <div class="ms-5 confirm-text">{{ withdrawStore.confirmData.account_name }}</div>
                    <div class="ms-5 me-4 bank-account">
                      {{ withdrawStore.confirmData.account_no }}
                    </div>
                  </div>
                  <hr/>
                  <div class="d-flex justify-content-start align-items-center mt-3 mb-3">
                    <div class="withdraw-label">Withdrawal fee</div>
                    <div class="ms-5 confirm-text">$ {{ formatAmount(withdrawStore.confirmData.withdrawal_fee) }}</div>
                  </div>
                </div>
              </div>
            </template>

            <!-- Step 5: Fail -->
            <template v-else-if="currentStep === 5">
              <h5 class="modal-title mb-3">Withdraw funds now</h5>
              <div class="review-title">
                <div class="fail-icon">✗</div>
                <div class="ms-3">Withdrawal unsuccessful</div>
              </div>

              <!-- 新增一個容器，用 flex 將內容置中 -->
              <div class="flex-grow-1 d-flex align-items-center justify-content-center flex-column h-50">
                <div class="review-content mt-4 text-center">
                  <div>
                    <div class="fail-text">
                      Sorry, we were unable to complete your withdrawal. Please contact our
                      customer service team for further information.
                    </div>
                  </div>

                  <div class="contact-info mt-5">
                    <div class="d-flex justify-content-center align-items-center">
                      <div>
                        <div class="d-flex justify-content-center align-items-center">
                            <img src="@/assets/images/driver/phone.png" alt="Phone" class="phone-icon" />
                            <div>
                                <strong>Call</strong> – 0280 060 696
                            </div>
                        </div>
                      </div>
                      <div class="ms-3">
                        <div class="mt-2 d-flex justify-content-center align-items-center">
                            <img src="@/assets/images/driver/sms.png" alt="SMS" class="sms-icon" />
                              <div>
                                <strong>SMS</strong> – 0466 048 009
                              </div>
                        </div>
                      </div>
                    </div>
                    <div class="d-flex justify-content-center align-items-center">
                      <div class="contact-time">(Monday to Friday 10:30am to 5pm)</div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <!-- 底部按鈕區：根據步驟改變按鈕顯示 -->
          <div class="d-flex justify-content-between align-items-center p-3" style="border-top: 1px solid #dee2e6;">
            <template v-if="currentStep === 1">
              <div>
                <button class="btn btn-cancel" @click="hideModal">Cancel</button>
              </div>
              <div>
                <button class="btn btn-green btn-ok" @click="goToStep2" :disabled="selectedTotal < 0">
                  Next
                  <img class="arrow-icon" src="@/assets/images/driver/next.png" />
                </button>
              </div>
            </template>
            <template v-else-if="currentStep === 2">
              <div>
                <button class="btn btn-cancel" @click="hideModal">Cancel</button>
                <button class="btn btn-green btn-ok ms-2" @click="backToStep1">
                  <img class="arrow-icon" src="@/assets/images/driver/back.png" />
                  Back
                </button>
              </div>
              <div>
                <button class="btn btn-green btn-ok" @click="confirmWithdrawal">
                  Confirm
                </button>
              </div>
            </template>
            <template v-else-if="currentStep === 4 || currentStep === 5">
              <div>&nbsp;</div>
              <div>
                <button class="btn btn-green btn-ok" @click="hideModal">
                  OK
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { Modal } from 'bootstrap'
import { useWithdrawStore } from '@/store/driver/withdraw'
import { useRouter } from 'vue-router'
import { fetchDriverInfo } from '@/store/public'
import Swal from 'sweetalert2'

const currentStep = ref(1)
const selectAll = ref(false) // 「全選」的狀態
const form = ref({})
const staffMemberModal = ref(null)
const nowTime = ref()
let modalInstance = null
const driverToken = localStorage.getItem('driverToken')

const withdrawStore = useWithdrawStore()


const router = useRouter()

// 頁數相關
const currentPage = ref(1)
const pageItems = ref(5)
const totalEntries = computed(() => withdrawStore.totalEntries)

const startTransaction = computed(() => (currentPage.value - 1) * pageItems.value + 1)
const endTransaction = computed(() => Math.min(currentPage.value * pageItems.value, withdrawStore.totalEntries))
const totalPages = computed(() => {
  return Math.ceil(totalEntries.value / pageItems.value)
})

const paginatedTransactions = computed(() => {
  const startIndex = (currentPage.value - 1) * pageItems.value
  const endIndex = startIndex + pageItems.value
  return withdrawStore.transactions.slice(startIndex, endIndex)
})

const showModal = async () => {
  await fetchDriverInfo(driverToken) // 重新更新 localStorage 中的 Driver
  const driver = JSON.parse(localStorage.getItem('Driver'))
  if (driver && driver.withdraw_enabled !== true) {
    return router.push('/')
  } else {
    currentStep.value = 1
    form.value = {}
    if (!modalInstance) {
      modalInstance = new Modal(staffMemberModal.value)
    }
    modalInstance.show()
  }
}

const hideModal = () => {
  if (modalInstance) {
    currentStep.value = 1
    changePage(1)
    withdrawStore.fetchTransactions()
    selectedTransactions.value.forEach(tx => tx.checked = false)
    modalInstance.hide()
  }
}

const goToProfiile = () => {
  hideModal()
  router.push('/profile')
}

const searchTransactions = () => {
  if (withdrawStore.filters.from && withdrawStore.filters.to) {
    withdrawStore.fetchTransactions()
  }
}

const goToStep2 = async () => {
  if (selectedCount.value === 0) {
    Swal.fire({
      icon: 'warning',
      title: 'Warning',
      text: 'Please select at least one transaction to proceed.',
      confirmButtonText: 'OK'
    })
    return
  }
  
  // 檢查 Payment Info 是否為負值
  if (selectedTotal.value < 0) {
    Swal.fire({
      icon: 'warning',
      title: 'Warning',
      text: 'Payment total cannot be negative. Please adjust your selection.',
      confirmButtonText: 'OK'
    })
    return
  }
  
  // 收集所有勾選交易的ID
  const transactionIds = selectedTransactions.value.map(tx => tx.id)

  currentStep.value = 3

  try {
    // 呼叫後端檢查
    const res = await withdrawStore.checkWithdraw(transactionIds)
   

    if (res) {
      currentStep.value = 2
    }else{
      currentStep.value = 1
    }
  } catch (error) {
    // ★ 如果後端檢查失敗 => 中斷流程、顯示錯誤
    Swal.fire({
      icon: 'error',
      title: 'Check failed',
      text: error.message || 'Something went wrong'
    })
  }
}

const backToStep1 = () => {
  currentStep.value = 1
}

const confirmWithdrawal = async () => {
  // 收集所有勾選交易的ID
  const transactionIds = selectedTransactions.value.map(tx => tx.id)

  currentStep.value = 3
  try {
    // 呼叫後端檢查
    const res = await withdrawStore.confirmWithdraw(transactionIds)

    if (res) {
      currentStep.value = 4
      nowTime.value = getAusTimeNow()
      withdrawStore.fetchWithdrawHistory()
    } else {
      currentStep.value = 5
      withdrawStore.fetchWithdrawHistory()
    }
  } catch (error) {
    // ★ 如果後端檢查失敗 => 中斷流程、顯示錯誤
    Swal.fire({
      icon: 'error',
      title: 'Check failed',
      text: error.message || 'Something went wrong'
    })
  }
}

/** ★ 新增防盜機制：監聽「全選」時，超過400的交易不勾選，負值交易自動勾選且不得取消 */
watch(selectAll, (newVal) => {
  withdrawStore.transactions.forEach(tx => {
    if (tx.transaction_total <= 400 && tx.transaction_total >= 0) {
      tx.checked = newVal
    } else if (tx.transaction_total < 0) {
      // 負值交易自動勾選且不得取消
      tx.checked = true
    } else {
      tx.checked = false
    }
  })
})

/** ★ 新增防盜機制：監聽「任一交易」的勾選變化 => 若可被勾選的交易都勾了，則 selectAll=true；否則 false */
watch(
  () => withdrawStore.transactions.map(t => t.checked),
  (newArr) => {
    // 只針對 <=400 且 >=0 的交易做「全勾」判斷（排除負值交易）
    const checkableTxs = withdrawStore.transactions.filter(t => t.transaction_total <= 400 && t.transaction_total >= 0)
    const allChecked = checkableTxs.length > 0 && checkableTxs.every(t => t.checked)
    selectAll.value = allChecked
  },
  { deep: true }
)

const changePage = (page) => {
  if (page < 1) page = 1
  if (page > totalPages.value) page = totalPages.value
  currentPage.value = page
}

onMounted(async () => {
  // 後端直接回傳全部交易
  await withdrawStore.fetchTransactions()
  // 幫新抓到的 transactions 設定 checked 狀態
  withdrawStore.transactions.forEach(tx => {
    if (tx.transaction_total < 0) {
      // 負值交易自動勾選且不得取消
      tx.checked = true
    } else {
      tx.checked = false
    }
  })
})

const selectedTransactions = computed(() => {
  return withdrawStore.transactions.filter(tx => tx.checked)
})

const selectedCount = computed(() => selectedTransactions.value.length)

const selectedSubTotal = computed(() => {
  return selectedTransactions.value.reduce((acc, tx) => {
    return acc + Number(tx.transaction_total || 0)
  }, 0)
})

// 取得目前選定的帳號（enabled == 1）
const currentAccount = computed(() => {
  try {
    const driver = JSON.parse(localStorage.getItem('Driver') || '{}')
    
    const accounts = driver?.accounts || []
    const enabledAccount = accounts.find(acc => acc.enabled == 1)
    
   
    
    return enabledAccount || null
  } catch (error) {
    console.error('❌ [currentAccount] 取得帳號資訊失敗:', error)
    return null
  }
})

const selectedFees = computed(() => {
  

  return withdrawStore.feesAndRefunds
})

const selectedTotal = computed(() => {
  // 計算所有選取交易的總金額
  const transactionsTotal = selectedTransactions.value.reduce((acc, tx) => {
    return acc + Number(tx.transaction_total || 0)
  }, 0)
  
    return transactionsTotal + selectedFees.value
})

function getAusTimeNow() {
  // 1) 先取得「以澳洲雪梨時區」為基準的當地時間字串
  const localString = new Date().toLocaleString('en-US', {
    timeZone: 'Australia/Sydney'
  })
  // 例如 "1/6/2024, 00:03:00"

  // 2) 轉成真正的 Date 物件
  const dateInSydney = new Date(localString)

  // 3) 取得「日 / 月(縮寫) / 年」
  const day = dateInSydney.getDate()
  const monthIndex = dateInSydney.getMonth() // 0 ~ 11
  const year = dateInSydney.getFullYear()
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun",
                      "Jul","Aug","Sep","Oct","Nov","Dec"]
  const monthShort = monthNames[monthIndex]

  const dayStr = day < 10 ? '0' + day : String(day)

  // 4) 取得「時 / 分」(12小時制) + am/pm
  let hour = dateInSydney.getHours()   // 0 ~ 23
  let minute = dateInSydney.getMinutes() // 0 ~ 59
  let ampm = 'am'
  if (hour >= 12) {
    ampm = 'pm'
    if (hour > 12) hour -= 12
  }
  if (hour === 0) hour = 12

  const minuteStr = minute < 10 ? '0' + minute : String(minute)

  // 5) 組裝成指定格式
  return `${dayStr} ${monthShort} ${year}, ${hour}:${minuteStr}${ampm} (AEST)`
}

// 格式化金額：將數值轉換為數字，並四捨五入至小數點後兩位
function formatAmount(value) {
  
  const num = Number(value) || 0
  return num.toFixed(2)
}
</script>

<style scoped>
.modal-content {
  height: auto;
  background-color: #fff;
  font-size: 14px;
  border-radius: 10px;
}

.modal-title{
  font-family: 'Euclid Square';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 25px;
  color: #051C2C;
}

.btn-green {
  background: rgba(0, 201, 98, 1);
  color: black;
  border: none;
  width: 220px;
  height: 40px;
  border-radius: 30px;
  font-size: 14px;
  padding: 8px 24px;
}
.btn-green:disabled {
  background: #ccc;
  color: #666;
  cursor: not-allowed;
}
.btn-green:hover {
  background-color: #00b452;
}
.btn-ok {
  width: 120px;
  font-size: 14px;
}
.btn-cancel {
  background-color: #fff;
  color: black;
  width: 120px;
  height: 40px;
  border-radius: 30px;
  border: 1px solid #8476f4;
  font-size: 14px;
  padding: 8px 24px;
}
.btn-cancel:hover {
  background-color: #e6e6ff;
}

/* Step1 */
.card-title {
  font-family: 'Euclid Square';
  font-style: normal;
  font-weight: 400;
  font-size: 22px;
  line-height: 30px;
  text-align: center;
  color: #051c2c;
  margin-bottom: 15px;
}
.bg-card {
  background-color: #e6e9ec;
  border-radius: 10px;
  margin-top: 15px;
  padding: 20px;
}
.text-label {
  color: #051c2c;
  font-weight: 400;
  font-size: 14px;
}
.custom-date-input {
  width: 130px;
  border-radius: 4px;
  color: #8476f4;
  border: 1px solid #8476f4;
  font-size: 14px;
  height: 34px;
}
.custom-date-input::-webkit-calendar-picker-indicator {
  color: #8476f4;
}
.select-all {
  padding: 5px 15px;
  background-color: #fff;
  border: 1px solid #fff;
  border-radius: 5px;
  font-family: 'Euclid Square';
  font-size: 14px;
  color: #051c2c;
}
.select-all-checkbox {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid #00c962;
  border-radius: 3px;
  background-color: #fff;
  cursor: pointer;
  outline: none;
  margin: 0;
}
.select-all-checkbox:checked {
  background-color: #00c962;
}
.select-all-checkbox:checked::after {
  content: '';
  display: block;
  position: relative;
  top: 1px;
  left: 4px;
  width: 5px;
  height: 9px;
  border: solid black;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.select-all-checkbox[disabled] {
  border-color: #c4c4c4;
  background-color: #c4c4c4;
  border: #00c962 1px solid;
  cursor: not-allowed;
}
.select-all-checkbox[disabled]:checked {
  background-color: #00c962;
  border-color: #00c962;
}


.table-content {
  margin-top: 10px;
}

/* 沒有交易紀錄時的樣式 */
.no-transactions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin: 20px 0;
}

.no-transactions-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.6;
}

.no-transactions-title {
  font-family: 'Euclid Square';
  font-style: normal;
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  color: #051c2c;
  margin-bottom: 8px;
}

.no-transactions-desc {
  font-family: 'Mulish';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: #666;
  max-width: 300px;
}
.table-responsive {
  margin-top: 10px;
}
.table th,
.table td {
  font-size: 14px;
  padding: 0.65rem;
  vertical-align: middle;
  border-top: 1px solid #dee2e6;
}
.table thead th {
  border-bottom: 2px solid #dee2e6;
}
.table-hover tbody tr:hover {
  color: #495057;
  background-color: rgba(0, 0, 0, 0.075);
}
.card-payment-info {
  margin-top: 15px;
}
.fee-note {
  font-size: 13px;
  color: #666;
  margin-top: 10px;
}
.arrow-icon {
  width: 13px;
  height: 12px;
}
.total-text {
  font-family: 'Euclid Square';
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  line-height: 25px;
  text-align: right;
  color: #051c2c;
}

/* Step2 Review & Confirm */
.review-title {
  font-size: 24px;
  font-weight: 500;
  color: #051c2c;
  margin: 40px 0;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}
.review-content {
  max-width: 100%;
  margin: 0 auto;
  margin-top: 100px;
  margin-bottom: 200px;
}
.success-icon {
  font-size: 48px;
  color: #00c962;
  margin-bottom: 5px;
}

.fail-icon {
  font-size: 48px;
  color: #ff5353;
  margin-bottom: 15px;
}

.fail-text {
  width: 600px;
  font-family: 'Euclid Square';
  font-style: normal;
  font-weight: 500;
  font-size: 24px;
  line-height: 30px;
  display: flex;
  align-items: center;
  text-align: center; 
  color: #051C2C;
  margin-bottom: 20px;
}
.contact-info {
  font-family: 'Euclid Square';
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  line-height: 25px;
  color: #051C2C;
}
.contact-time {
  margin-top: 20px;
  font-family: 'Mulish';
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  line-height: 23px;
  display: flex;
  align-items: center;
  text-align: center;
  color: #051C2C;
}

.phone-icon {
  margin-right: 20px;
  width: 19px;
  height: 34px;
}

.sms-icon {
  margin-right: 20px;
  width: 26px;
  height: 34px;
}

.confirm-text{
  font-family: 'Euclid Square';
  font-style: normal;
  font-weight: 500;
  font-size: 20px;
  line-height: 25px;
  display: flex;
  align-items: center;
  color: #051C2C;
}

.withdraw-label {
  width: 100px;
  font-size: 16px;
  font-weight: 400;
  color: #051c2c;
  text-align: left;
}
.withdraw-amount {
  font-size: 42px;
  font-weight: 600;
  color: #00c962;
  text-align: center;
}

.bank-account {
  color: #051c2c;
  font-size: 14px;
  font-weight: 400;
  min-width: 100px;
  text-align: left;
}
.bank-hint {
  width: 100%;
  font-size: 14px;
  color: #666;
  line-height: 1.4;
}
.bank-hint a {
  color: #8476f4;
  text-decoration: none;
}

.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
}

.pagination-container {
  display: flex;
  align-items: center;
}

.pagination-container button {
  border: none;
  background: none;
  cursor: pointer;
  font-size: 16px;
  color: #8476f4;
}

.pagination-container span {
  margin: 0 10px;
}

.pagination-container input {
  width: 50px;
  text-align: center;
}

/* Loading (step3) */
.spinner {
  width: 60px;
  height: 60px;
  border: 6px solid #ccc;
  border-top: 6px solid #00c962;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
